import { useEffect, useState, useRef } from "react";
import { Loader2, MapPin, Building2 } from "lucide-react";

/**
 * PLZ + Ort Eingabefelder mit Auto-Lookup via openplzapi.org (DE).
 */
export const PlzCityInput = ({ zip, city, onZipChange, onCityChange, zipTestId = "zip-input", cityTestId = "city-input" }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const lastZipRef = useRef("");

  useEffect(() => {
    if (!zip || zip.length !== 5 || !/^\d{5}$/.test(zip)) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (lastZipRef.current === zip) return;
    lastZipRef.current = zip;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await fetch(`https://openplzapi.org/de/Localities?postalCode=${zip}`);
        if (!resp.ok) throw new Error("API failed");
        const data = await resp.json();
        const unique = Array.from(new Set((data || []).map((d) => d.name))).filter(Boolean);
        setSuggestions(unique);
        // Nur auto-befüllen / Dropdown öffnen, wenn der Ort noch leer ist
        // (z.B. Neuanlage). Beim Wieder-Aufrufen eines existierenden Kunden
        // ist `city` bereits gesetzt und der User soll NICHT wieder gefragt
        // werden, weil das wie ein offener Pflicht-Schritt wirkt.
        const cityAlreadySet = !!(city && city.trim());
        if (unique.length === 1 && !cityAlreadySet) {
          onCityChange(unique[0]);
          setOpen(false);
        } else if (unique.length > 1 && !cityAlreadySet) {
          setOpen(true);
        } else {
          setOpen(false);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [zip]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div>
        <label className="block text-[13px] font-semibold text-zinc-100 mb-2">PLZ</label>
        <input
          data-testid={zipTestId}
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={zip || ""}
          onChange={(e) => onZipChange(e.target.value.replace(/[^\d]/g, ""))}
          className="h-12 w-full rounded-xl px-4 text-[15px] focus:ring-1 transition outline-none border"
          placeholder="z. B. 14195"
        />
      </div>
      <div className="md:col-span-2 relative">
        <label className="flex items-center justify-between text-[13px] font-semibold text-zinc-100 mb-2">
          <span>Ort</span>
          {loading && <span className="text-[12px] text-[#00E575] inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Suche…</span>}
          {!loading && suggestions.length > 0 && (
            <span className="text-[12px] text-[#00E575] font-mono">{suggestions.length} Treffer</span>
          )}
        </label>
        <div className="relative">
          <input
            data-testid={cityTestId}
            type="text"
            value={city || ""}
            onChange={(e) => onCityChange(e.target.value)}
            onFocus={() => suggestions.length > 1 && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 180)}
            className="h-12 w-full rounded-xl px-4 text-[15px] focus:ring-1 transition outline-none border"
            placeholder={suggestions.length === 0 ? "Ort eingeben" : "Bitte wählen…"}
          />
          {suggestions.length > 0 && (
            <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 text-[#00E575] pointer-events-none" size={16} />
          )}
        </div>
        {open && suggestions.length > 1 && (
          <div className="absolute z-50 mt-1 w-full bg-[#1F1F26] border border-[#4B4B55] rounded-xl shadow-xl max-h-64 overflow-y-auto">
            {suggestions.map((s) => (
              <button
                type="button"
                key={s}
                data-testid={`plz-suggestion-${s}`}
                onMouseDown={(e) => { e.preventDefault(); onCityChange(s); setOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-[14px] hover:bg-[#00E575]/10 hover:text-[#00E575] transition border-b border-zinc-800 last:border-0"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

/**
 * Straßen-Autocomplete via openplzapi.org/de/Streets (DE).
 * Triggert bei PLZ (5-stellig) + Straße (mind. 2 Zeichen).
 */
export const StreetAutocomplete = ({ zip, street, onStreetChange, testid = "street-input" }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef("");

  useEffect(() => {
    const hasZip = zip && zip.length === 5 && /^\d{5}$/.test(zip);
    const hasStreet = street && street.length >= 2;
    if (!hasZip || !hasStreet) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const query = `${zip}|${street}`;
    if (lastQueryRef.current === query) return;
    lastQueryRef.current = query;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://openplzapi.org/de/Streets?postalCode=${zip}&name=${encodeURIComponent(street)}`;
        const resp = await fetch(url);
        if (!resp.ok) throw new Error("API failed");
        const data = await resp.json();
        // Build dedup list of street objects with name + suburb context
        const seen = new Set();
        const list = [];
        for (const s of data || []) {
          const name = s.name;
          if (!name) continue;
          const k = `${name}|${s.suburb || ""}`;
          if (seen.has(k)) continue;
          seen.add(k);
          list.push({ name, suburb: s.suburb, locality: s.locality });
        }
        setSuggestions(list.slice(0, 10));
        // Only auto-open if user is typing prefix that doesn't already match suggestion exactly
        const exactMatch = list.find((x) => x.name.toLowerCase() === (street || "").toLowerCase());
        setOpen(list.length > 0 && !exactMatch);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [zip, street]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasZip = zip && zip.length === 5;

  return (
    <div className="relative">
      <label className="flex items-center justify-between text-[13px] font-semibold text-zinc-100 mb-2">
        <span>Straße</span>
        {hasZip && loading && <span className="text-[12px] text-[#00E575] inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Suche…</span>}
        {hasZip && !loading && suggestions.length > 0 && open && (
          <span className="text-[12px] text-[#00E575] font-mono">{suggestions.length} Vorschl.</span>
        )}
      </label>
      <div className="relative">
        <input
          data-testid={testid}
          type="text"
          value={street || ""}
          onChange={(e) => onStreetChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          className="h-12 w-full rounded-xl px-4 text-[15px] focus:ring-1 transition outline-none border"
          placeholder={hasZip ? "Straße eingeben (Vorschläge erscheinen)" : "Erst PLZ eintragen für Vorschläge"}
          autoComplete="off"
        />
        {hasZip && suggestions.length > 0 && (
          <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 text-[#00E575] pointer-events-none" size={16} />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-[#1F1F26] border border-[#4B4B55] rounded-xl shadow-xl max-h-72 overflow-y-auto">
          {suggestions.map((s, idx) => (
            <button
              type="button"
              key={`${s.name}-${idx}`}
              data-testid={`street-suggestion-${idx}`}
              onMouseDown={(e) => { e.preventDefault(); onStreetChange(s.name); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 hover:bg-[#00E575]/10 transition border-b border-zinc-800 last:border-0 group"
            >
              <div className="text-[14px] text-zinc-50 font-medium group-hover:text-[#00E575]">{s.name}</div>
              {(s.suburb || s.locality) && (
                <div className="text-[12px] text-zinc-400 mt-0.5">
                  {s.suburb && <span>{s.suburb}</span>}
                  {s.suburb && s.locality && s.locality !== s.suburb && <span> · {s.locality}</span>}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
